import React, { useState, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import axios from 'axios'

import Header from '../header/Header'
import ProductCard from '../catalogue/nodes/ProductCard'
import FolderProduct from '../catalogue/nodes/FolderProduct'
import Spinner from '../common/Spinner'
import LoadingSpinner from '../common/LoadingSpinner'
import { Breadcrumb, HeaderBreadcrumb, BreadcrumbPortal } from '../common/Breadcrumb'
import { BusinessUnitMultiSelect } from '../fields'
import { articleSearchApi } from '../../api/articleSearchApi'
import { routes } from '../../constantes/routes'
import '../../assests/styles/catalogue/CatalogueCards.scss'
import '../../assests/styles/catalogue/NodesV2.scss'
// Los mismos que carga CatalogueContainerV2, para no depender de que esté
// montado en otra parte del árbol.
import '../../assests/styles/catalogue/catalogueButtons.scss'
import '../../assests/styles/catalogue/ProductCard.scss'

/**
 * Nivel raíz del Catálogo con el motor de búsqueda v2.
 *
 * Sustituye ÚNICAMENTE la búsqueda global del primer nivel, que es la que
 * recorre el árbol entero: una petición por categoría más una por cada nodo
 * carpeta, 485 en esta instalación, en cada búsqueda y sin importar cuántos
 * resultados haya. Aquí es una sola petición.
 *
 * La navegación (entrar en una categoría, en una familia, ver la ficha) sigue
 * siendo la de siempre: CatalogueSearchHost deja pasar esas rutas al catálogo
 * original sin tocarlo. El filtrado local de los niveles profundos también se
 * mantiene, porque opera sobre datos ya cargados y no tiene ningún problema.
 */

const TAM_PAGINA = 40
const MARGEN = 300

/** Mismo saneado de nombre que usa el catálogo actual para construir la URL. */
const limpiarNombre = (nombre) => (nombre || '').replace(/ *\([^)]*\) */g, '')

/**
 * Quita los acentos para comparar.
 *
 * La búsqueda del servidor usa la intercalación de SQL Server, que es
 * insensible a acentos: "aguja" encuentra "AGUJA" y "cirugia" encuentra
 * "cirugía". El `includes` de JavaScript no. Sin esto, las categorías se
 * comportarían distinto que los artículos en la misma caja de búsqueda.
 */
const sinAcentos = (texto) =>
  (texto || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')

const CatalogueRootV2 = () => {
  const history = useHistory()
  const categoriasOriginales = useSelector((estado) => estado.categories)

  const [unidades, setUnidades] = useState([])
  const [texto, setTexto] = useState('')
  const [termino, setTermino] = useState('')
  const [pagina, setPagina] = useState(1)

  const [resultados, setResultados] = useState([])
  const [total, setTotal] = useState(0)
  // El universo sobre el que se busca, sin contar el término.
  const [totalGeneral, setTotalGeneral] = useState(null)
  const [hayMas, setHayMas] = useState(false)
  const [cargando, setCargando] = useState(false)
  const [error, setError] = useState(null)
  const [ms, setMs] = useState(null)

  // Qué página se está pidiendo (0 = ninguna): distingue la primera carga de
  // las siguientes, para no rotular "Cargando más…" en la búsqueda inicial.
  const [paginaCargando, setPaginaCargando] = useState(0)

  const cargandoRef = useRef(false)
  const hayMasRef = useRef(false)
  const centinelaRef = useRef(null)
  const peticionRef = useRef(0)

  const buscando = termino !== ''
  const unidadesTexto = unidades.join(',')

  // Debounce.
  useEffect(() => {
    const t = setTimeout(() => setTermino(texto.trim()), 350)
    return () => clearTimeout(t)
  }, [texto])

  // Al cambiar término o unidades se empieza de cero.
  useEffect(() => {
    setPagina(1)
    setResultados([])
    setTotal(0)
    setHayMas(false)
    hayMasRef.current = false
  }, [termino, unidadesTexto])

  // Búsqueda. Una sola petición, cancelable.
  useEffect(() => {
    if (!buscando) {
      setCargando(false)
      cargandoRef.current = false
      setPaginaCargando(0)
      return undefined
    }

    const fuente = axios.CancelToken.source()
    const idPeticion = peticionRef.current + 1
    peticionRef.current = idPeticion

    const paginaPedida = pagina
    const t0 = performance.now()

    setCargando(true)
    cargandoRef.current = true
    setPaginaCargando(paginaPedida)
    setError(null)

    const terminar = () => {
      setCargando(false)
      cargandoRef.current = false
      setPaginaCargando(0)
    }

    articleSearchApi
      .search(
        {
          q: termino,
          scope: 'all',
          page: paginaPedida,
          pageSize: TAM_PAGINA,
          businessUnits: unidadesTexto,
        },
        fuente.token,
      )
      .then((res) => {
        if (idPeticion !== peticionRef.current) return

        const datos = res.data
        setResultados((previos) =>
          paginaPedida === 1 ? datos.items : previos.concat(datos.items),
        )
        setTotal(datos.total)
        setTotalGeneral(datos.totalSinFiltro)
        setHayMas(datos.hasMore)
        hayMasRef.current = datos.hasMore
        setMs(Math.round(performance.now() - t0))
        terminar()
      })
      .catch((err) => {
        if (idPeticion !== peticionRef.current) return

        if (!axios.isCancel(err)) {
          const codigo = err && err.response ? err.response.status : '—'
          setError(`No se ha podido buscar (HTTP ${codigo}).`)
          setHayMas(false)
          hayMasRef.current = false
        }
        terminar()
      })

    return () => {
      peticionRef.current += 1
      fuente.cancel()
    }
  }, [termino, unidadesTexto, pagina, buscando])

  /**
   * El universo, aparte de la búsqueda.
   *
   * Sin término no se lanza la búsqueda —se pintan las categorías—, así que
   * sin esto no habría forma de saber cuántos elementos hay hasta que el
   * usuario escribiera algo. Se pide con pageSize=1 porque sólo interesa el
   * recuento, y se rehace únicamente al cambiar el filtro de unidades.
   *
   * Va en su propio efecto para no enredarse con el estado de carga de la
   * búsqueda: no toca ni `cargando` ni `resultados`.
   */
  useEffect(() => {
    const fuente = axios.CancelToken.source()

    articleSearchApi
      .search(
        { scope: 'all', page: 1, pageSize: 1, businessUnits: unidadesTexto },
        fuente.token,
      )
      .then((res) => setTotalGeneral(res.data.totalSinFiltro))
      .catch(() => {
        // Sin este dato el contador simplemente no lo muestra.
      })

    return () => fuente.cancel()
  }, [unidadesTexto])

  // Centinela para cargar más al llegar al final de la rejilla.
  useEffect(() => {
    const centinela = centinelaRef.current
    if (!centinela || typeof IntersectionObserver === 'undefined') return undefined

    const observador = new IntersectionObserver(
      (entradas) => {
        if (
          entradas.some((e) => e.isIntersecting) &&
          !cargandoRef.current &&
          hayMasRef.current
        ) {
          setPagina((p) => p + 1)
        }
      },
      { rootMargin: `0px 0px ${MARGEN}px 0px` },
    )

    observador.observe(centinela)
    return () => observador.disconnect()
  }, [])

  // Sin búsqueda: las categorías del usuario, filtradas por unidad de negocio.
  const categorias =
    unidades.length === 0
      ? categoriasOriginales
      : categoriasOriginales.filter((c) =>
          Array.isArray(c.id_NegocioUnidades)
            ? c.id_NegocioUnidades.some((id) => unidades.includes(id))
            : unidades.includes(c.id_NegocioUnidades),
        )

  /**
   * Categorías cuyo NOMBRE coincide con lo buscado.
   *
   * El servidor busca en Articulos.Nombre y Referencia; las categorías son
   * otra tabla y nunca entrarían. El catálogo antiguo sí las incluía —las
   * filtraba en cliente— y al sustituir el recorrido recursivo por la llamada
   * al servidor se perdió. Se restaura aquí, y sale gratis: las 74 categorías
   * ya están en Redux, no hay que pedir nada.
   */
  const categoriasCoincidentes = buscando
    ? categorias.filter((c) => sinAcentos(c.nombre).includes(sinAcentos(termino)))
    : []

  // "elementos" y no "productos": en el Catálogo el ámbito es todo, así que
  // el universo incluye las famílias y subfamílias además de los finales.
  const recuento = [
    totalGeneral !== null ? `${totalGeneral} elementos` : null,
    buscando && categoriasCoincidentes.length
      ? `${categoriasCoincidentes.length} categorías`
      : null,
    buscando ? `${total} coincidencias` : null,
    buscando && resultados.length < total
      ? `mostrando ${resultados.length}`
      : null,
  ]
    .filter(Boolean)
    .join(' · ')

  const pintarCategoria = (categoria) => (
    <FolderProduct
      key={`v2-cat-${categoria.id_TipoCategoria}`}
      item={categoria}
      pathname={`${routes.CATALOGUE_V2}/categoria/${categoria.id_TipoCategoria}`}
    />
  )

  const pintarResultado = (item) =>
    item.isArticuloFinal ? (
      <ProductCard
        key={`v2-final-${item.id_Articulo}`}
        item={item}
        pathname={`${routes.CATALOGUE_V2}/producto-final/${item.id_Articulo}`}
      />
    ) : (
      <FolderProduct
        key={`v2-folder-${item.id_Articulo}`}
        item={item}
        pathname={`${routes.CATALOGUE_V2}/producto/${
          item.id_Articulo
        }/${encodeURIComponent(limpiarNombre(item.nombre))}`}
      />
    )

  return (
    // El mismo envoltorio que usa CatalogueContainerV2. La clase .products
    // aporta padding: 70px 3vw 0 3vw — los 70px que despejan la cabecera fija
    // y el margen lateral. Sin ella la página se mete debajo del header.
    <div className='products'>
      {/* Mismo rótulo que el catálogo actual, a propósito: durante la
          evaluación conviene que la única diferencia visible sea el buscador,
          no la nomenclatura. Se puede acortar cuando se retire el motor viejo. */}
      <Breadcrumb to={routes.CATALOGUE_V2}>Catálogo - Búsqueda Global</Breadcrumb>

      {/* El mismo rótulo que usa NodesV2 en el nivel raíz. Los niveles
          profundos ponen sólo "Catálogo", y de esos sigue encargándose él. */}
      <Header
        text={'Catálogo - Búsqueda Global'}
        route={() => history.goBack()}
        backBtn={true}
      />

      <div className='container-fluid m-0 p-0'>
        <div className='row'>
          <div className='col-sm-12 col-xl-8 justify-content-start d-flex'>
            <HeaderBreadcrumb>
              {/* El contador va en la misma línea que la miga de pan, no en
                  una fila propia: esa línea ya existe, así que no añade alto.
                  El flex es lo que impide que el <nav> del portal, que es de
                  bloque, empuje el contador al renglón siguiente.
                  Hereda tamaño y color de HeaderBreadcrumb. */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 10,
                  minWidth: 0,
                }}
              >
                <BreadcrumbPortal />

                {recuento && !error ? (
                  // 14px es el tamaño que usa la aplicación para texto
                  // secundario; HeaderBreadcrumb va a 18px, así que el
                  // contador queda por debajo y se lee como un dato, no
                  // como parte de la ruta de navegación.
                  <span
                    style={{
                      whiteSpace: 'nowrap',
                      fontSize: 14,
                      opacity: 0.75,
                    }}
                  >
                    · {recuento}
                    {/* Tiempo de respuesta. Útil mientras se compara con el
                        motor antiguo, pero no es información para el usuario.
                        Descomentar para volver a medir en pantalla; el estado
                        `ms` se sigue calculando. */}
                    {/* {ms !== null ? ` · ${ms} ms` : ''} */}
                  </span>
                ) : null}
              </div>
            </HeaderBreadcrumb>
          </div>

          <div className='col-sm-12 col-xl-4 justify-content-end d-flex'>
            <div className='w-100 d-flex'>
              <div style={{ marginRight: '1rem' }}>
                <BusinessUnitMultiSelect
                  size='xl'
                  value={unidades}
                  onChange={setUnidades}
                  placeholder='Selecciona las unidades de negocio...'
                  compact
                />
              </div>

              <div className='search' style={{ minWidth: '120px' }}>
                <div className='searchbar__wrap'>
                  <h4 className='filter__title'>Buscador</h4>
                  <input
                    className='btn-filter search placeholder-text'
                    type='text'
                    value={texto}
                    placeholder='Buscar productos...'
                    onChange={(e) => setTexto(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* El error sí ocupa sitio: es excepcional y tiene que verse. */}
        {error ? (
          <div
            style={{
              margin: '10px 0',
              padding: '8px 12px',
              fontSize: 13.5,
              fontWeight: 600,
              color: '#a32218',
              background: '#f6e3e1',
              borderLeft: '3px solid #a32218',
            }}
          >
            {error}
          </div>
        ) : null}

        <div className='row'>
          <div className='col-12'>
            {cargando && resultados.length === 0 ? (
              // El mismo que tenía el catálogo antes. El texto sigue siendo
              // cierto —y más que antes—: v2 busca en categorías, famílias,
              // subfamílias y productos finales, sólo que en una consulta en
              // vez de recorriendo el árbol con 485 peticiones.
              <LoadingSpinner
                message='Buscando en todos los niveles...'
                size='large'
                variant='primary'
              />
            ) : buscando ? (
              categoriasCoincidentes.length > 0 || resultados.length > 0 ? (
                // Las categorías primero: son el contenedor, y quien busca
                // "Gestión de Material Sanitario" quiere entrar ahí, no ver
                // los productos sueltos que lleven esas palabras.
                <div className='catalogue-grid'>
                  {categoriasCoincidentes.map(pintarCategoria)}
                  {resultados.map(pintarResultado)}
                </div>
              ) : (
                <div className='catalogue-no-results'>
                  <div className='no-results-content'>
                    <div className='no-results-icon'>🔍</div>
                    <div className='no-results-text'>
                      No se encontraron resultados
                    </div>
                  </div>
                </div>
              )
            ) : categorias.length > 0 ? (
              <div className='catalogue-grid'>{categorias.map(pintarCategoria)}</div>
            ) : (
              <div className='catalogue-no-results'>
                <div className='no-results-content'>
                  <div className='no-results-icon'>🔍</div>
                  <div className='no-results-text'>No hay productos disponibles</div>
                </div>
              </div>
            )}

            <div ref={centinelaRef} style={{ height: 1 }} />

            <div style={{ textAlign: 'center', padding: '16px 0' }}>
              {paginaCargando > 1 ? <Spinner texto='Cargando más...' /> : null}

              {!cargando && hayMas ? (
                <button
                  className='user-input btn-shape'
                  onClick={() => setPagina((p) => p + 1)}
                >
                  Cargar más ({total - resultados.length} restantes)
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CatalogueRootV2
